import * as THREE from "three";
import {
  BlendFunction,
  BloomEffect,
  Effect,
  EffectComposer,
  EffectPass,
  HueSaturationEffect,
  RenderPass,
  SMAAEffect,
  ToneMappingEffect,
  ToneMappingMode,
  VignetteEffect,
} from "postprocessing";
import { N8AOPostPass } from "n8ao";

// --- Sensor overlay shader ------------------------------------------------
// Applies: colour grade (white-hot thermal), vignette, film grain, scanlines

const ThermalFragmentShader = /* glsl */ `
    uniform float uGrain;
    uniform float uVignette;
    uniform float uScanlines;
    uniform int uMode;

    float rand(vec2 co) {
      return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453 + time * 0.1);
    }

    void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
      vec4 col = inputColor;

      // Thermal transfer curve, tuned for a correctly-lit tone-mapped input:
      // terrain midtones (~0.3-0.5 lum) sit dim, emissives push toward white.
      // (The old lift curve assumed the pre-fix murky linear scene and now
      // washes everything to flat grey.)
      float lum = dot(col.rgb, vec3(0.299, 0.587, 0.114));
      float boosted = pow(clamp(lum * 0.85 + 0.03, 0.0, 1.0), 1.35);

      // Detect colored emissives (saturated + bright) for IFF color preservation
      float maxChan = max(max(col.r, col.g), col.b);
      float minChan = min(min(col.r, col.g), col.b);
      float saturation = maxChan > 0.001 ? (maxChan - minChan) / maxChan : 0.0;
      // Bright + saturated = emissive unit (squad/enemy marker). Preserve their hue.
      float iffMask = smoothstep(0.2, 0.5, saturation) * smoothstep(0.15, 0.4, lum);

      // Sensor colour grade
      vec3 graded;
      if (uMode == 0) {
        // White-hot: terrain/cold = dim green-grey, hot objects = white
        graded = mix(vec3(0.06, 0.12, 0.08), vec3(1.0, 1.0, 0.95), boosted);
      } else if (uMode == 1) {
        // Black-hot: inverted
        graded = mix(vec3(0.92, 0.92, 0.88), vec3(0.02, 0.04, 0.02), boosted);
      } else if (uMode == 2) {
        // EO green night
        graded = vec3(0.0, boosted * 0.9 + 0.06, 0.0);
      } else {
        // Daylight — pass colour through unmodified, slight contrast lift
        graded = pow(col.rgb, vec3(0.9));
      }

      // Blend original color into thermal for emissive IFF targets — keeps squad blue, enemies red
      // Skip the blend in daylight mode (colour already correct)
      if (uMode != 3) {
        graded = mix(graded, col.rgb * (boosted + 0.5), iffMask * 0.65);
      }

      // Scanlines — suppress in daylight
      float scanStrength = uMode == 3 ? 0.0 : uScanlines;
      float scan = 1.0 - scanStrength * step(0.5, fract(uv.y * 480.0));
      graded *= scan;

      // Film grain — minimal in daylight
      float grainStrength = uMode == 3 ? uGrain * 0.3 : uGrain;
      graded += (rand(uv) - 0.5) * grainStrength;

      // Vignette
      vec2 uv2 = uv * 2.0 - 1.0;
      float vigStrength = uMode == 3 ? uVignette * 0.5 : uVignette;
      float vig = 1.0 - vigStrength * dot(uv2, uv2);
      graded *= clamp(vig, 0.0, 1.0);

      outputColor = vec4(clamp(graded, 0.0, 1.0), col.a);
    }
  `;

export type SensorMode = "white-hot" | "black-hot" | "eo-green" | "daylight";

class ThermalEffect extends Effect {
  constructor() {
    super("ThermalEffect", ThermalFragmentShader, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, THREE.Uniform>([
        ["uGrain", new THREE.Uniform(0.016)],
        ["uVignette", new THREE.Uniform(0.18)],
        ["uScanlines", new THREE.Uniform(0.025)],
        ["uMode", new THREE.Uniform(0)],
      ]),
    });
  }

  setMode(mode: SensorMode): void {
    const modeMap: Record<SensorMode, number> = {
      "white-hot": 0,
      "black-hot": 1,
      "eo-green": 2,
      "daylight": 3,
    };
    this.uniforms.get("uMode")!.value = modeMap[mode];
  }
}

export class PostProcessor {
  readonly composer: EffectComposer;
  private readonly thermalEffect: ThermalEffect;

  constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) {
    // MSAA is off because N8AO needs the depth buffer; SMAA (below) takes
    // over antialiasing.
    this.composer = new EffectComposer(renderer, {
      frameBufferType: THREE.HalfFloatType,
    });
    this.composer.addPass(new RenderPass(scene, camera));

    // Half-res contact AO grounds the settlements — buildings sit IN the
    // terrain instead of on it. Radius tuned to building scale, not vista
    // scale, so it never murks the aerial read.
    const size = renderer.getSize(new THREE.Vector2());
    const n8ao = new N8AOPostPass(scene, camera, size.x, size.y);
    n8ao.configuration.halfRes = true;
    n8ao.configuration.aoRadius = 5;
    n8ao.configuration.distanceFalloff = 3;
    n8ao.configuration.intensity = 2.6;
    // Output stays linear — ACES + the sensor grade happen downstream.
    n8ao.configuration.gammaCorrection = false;
    this.composer.addPass(n8ao);

    const smaa = new SMAAEffect();

    // Threshold sits above diffuse daylight so only genuinely hot pixels
    // (emissive IFF markers, muzzle flash, explosions, the sun) bloom — a
    // 0.05 threshold bloomed the entire frame into milk once the scene was
    // correctly lit.
    const bloom = new BloomEffect({
      intensity: 0.45,
      luminanceThreshold: 0.75,
      luminanceSmoothing: 0.08,
      mipmapBlur: true,
      radius: 0.5,
    });

    // HDR radiance (HDRI sky, bloom) → LDR before the sensor grade. The
    // renderer keeps NoToneMapping per the postprocessing linear-workflow
    // contract; this effect is the pipeline's tone mapper.
    const toneMapping = new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC });

    // Grade: a small saturation push sells the stylized palette; the subtle
    // vignette frames the sensor view.
    const saturation = new HueSaturationEffect({ saturation: 0.1 });
    const vignette = new VignetteEffect({ offset: 0.28, darkness: 0.42 });

    this.thermalEffect = new ThermalEffect();
    this.composer.addPass(new EffectPass(camera, smaa, bloom, toneMapping, saturation, vignette, this.thermalEffect));
  }

  setMode(mode: SensorMode): void {
    this.thermalEffect.setMode(mode);
  }

  resize(w: number, h: number): void {
    this.composer.setSize(w, h);
  }

  render(dt: number): void {
    this.composer.render(dt);
  }
}
